import { I13N_CATEGORY_CONSUMABLES, I13N_CATEGORY_SUBSCRIPTION } from 'Lib/constants/i13nSpec';
import type { GlobalStateType } from 'Lib/reduxType';

import { getProfilesActivePhotoIndexById } from 'Store/attributes/selectors';
import {
  PRODUCT_TYPE_BOOST,
  PRODUCT_TYPE_BOOST_SUBSCRIPTION,
  PRODUCT_TYPE_BOUNCER_BYPASS,
  PRODUCT_TYPE_GOLD,
  PRODUCT_TYPE_PLATINUM,
  PRODUCT_TYPE_PLUS,
  PRODUCT_TYPE_PRIMETIME_BOOST,
  PRODUCT_TYPE_READ_RECEIPT,
  PRODUCT_TYPE_SELECT_SUBSCRIPTION,
  PRODUCT_TYPE_SUPERBOOST,
  PRODUCT_TYPE_SUPERLIKE,
  PRODUCT_TYPES_ALL_SUBSCRIPTION,
  PURCHASE_TYPE_CONSUMABLE,
  PURCHASE_TYPE_SUBSCRIPTION,
} from 'Store/entities/products.schema';
import type { PaymentType, Product } from 'Store/entities/products.types';
import { getProductSkuId } from 'Store/entities/products.utils';
import {
  getIncentiveCampaignId,
  getIncentiveCampaignName,
  getIncentiveCCRequired,
  getIncentivePromoSource,
  getIsFreeTrialIncentiveCampaign,
  getIsIntroPriceIncentiveCampaign,
} from 'Store/incentives/selectors';
import { getLikesYouCount } from 'Store/likesYou/selectors';
import {
  getHasFreeTrialIncentiveSubscription,
  getHasIntroPriceIncentiveSubscription,
} from 'Store/profile/revenue.selectors';
import { PAYMENT_TYPE_CREDIT_CARD } from 'Store/purchase/constants';
import { getCCType, getPaymentMethod, getSaveCardBitwise } from 'Store/purchase/selectors';
import { getCurrentRec } from 'Store/recs/selectors';
import { getIncentivePromoCode } from 'Store/user/selectors';

import {
  EVENT_PRODUCT_TYPE_BOOST,
  EVENT_PRODUCT_TYPE_BOOST_SUBSCRIPTION,
  EVENT_PRODUCT_TYPE_BOUNCER_BYPASS,
  EVENT_PRODUCT_TYPE_GOLD,
  EVENT_PRODUCT_TYPE_PLATINUM,
  EVENT_PRODUCT_TYPE_PLUS,
  EVENT_PRODUCT_TYPE_PRIMETIME_BOOST,
  EVENT_PRODUCT_TYPE_READ_RECEIPTS,
  EVENT_PRODUCT_TYPE_SELECT_SUBSCRIPTION,
  EVENT_PRODUCT_TYPE_SUPERBOOST,
  EVENT_PRODUCT_TYPE_SUPERLIKE,
  PAGE_VERSION_CHECKOUT,
  SOURCE_SESSION_EVENT_REVENUE,
} from 'Store/sparks/lib/constants';
import { getCommonData } from 'Store/sparks/lib/dataUtils';
import type { EventData as RevenuePurchaseFlowProductEventData } from 'Store/sparks/revenue/events/revenuePurchaseFlow';

export const analyticsProductType = {
  [PRODUCT_TYPE_BOOST]: EVENT_PRODUCT_TYPE_BOOST,
  [PRODUCT_TYPE_BOUNCER_BYPASS]: EVENT_PRODUCT_TYPE_BOUNCER_BYPASS,
  [PRODUCT_TYPE_GOLD]: EVENT_PRODUCT_TYPE_GOLD,
  [PRODUCT_TYPE_PLATINUM]: EVENT_PRODUCT_TYPE_PLATINUM,
  [PRODUCT_TYPE_PLUS]: EVENT_PRODUCT_TYPE_PLUS,
  [PRODUCT_TYPE_PRIMETIME_BOOST]: EVENT_PRODUCT_TYPE_PRIMETIME_BOOST,
  [PRODUCT_TYPE_READ_RECEIPT]: EVENT_PRODUCT_TYPE_READ_RECEIPTS,
  [PRODUCT_TYPE_SUPERLIKE]: EVENT_PRODUCT_TYPE_SUPERLIKE,
  [PRODUCT_TYPE_SUPERBOOST]: EVENT_PRODUCT_TYPE_SUPERBOOST,
  [PRODUCT_TYPE_BOOST_SUBSCRIPTION]: EVENT_PRODUCT_TYPE_BOOST_SUBSCRIPTION,
  [PRODUCT_TYPE_SELECT_SUBSCRIPTION]: EVENT_PRODUCT_TYPE_SELECT_SUBSCRIPTION,
};

export const getPurchaseTax = (product: any) =>
  Math.round(product.taxRate * product.price * 100) / 100;

export const getPurchasePrice = (product: any) =>
  Math.round((product.price / (product.amount || 1)) * 100) / 100;

export const getSkuPart = (sku: string, index: number) => sku.split('_')[index];

const getSubscriptionValue = (subType) => {
  switch (subType) {
    case PRODUCT_TYPE_GOLD:
      return 2;
    case PRODUCT_TYPE_PLATINUM:
      return 3;
    case PRODUCT_TYPE_PLUS:
      return 1;
    default:
      return 0;
  }
};

// Maps immutable list back to an object keyed by product ID to preserve existing PaywallSparks event processing
/* eslint-disable no-undef */
// $FlowFixMe cannot-resolve-module
const productsToObject = (products: Product[] = []): Record<string, Product> =>
  products.reduce(
    (productsById, product) => Object.assign(productsById, { [product.productId]: product }),
    {}
  );
/* eslint-enable no-undef */

export const getRevenuePurchaseFlowProductEventData = (
  eventData: any = {},
  product: Product,
  state: GlobalStateType,
  paymentType: PaymentType = PAYMENT_TYPE_CREDIT_CARD
): RevenuePurchaseFlowProductEventData => {
  const { amount, termsUnit, pricePerTerm, currency } = product ?? {};
  const result = {
    action: eventData.action,
    actionContext: eventData.actionContext ?? null,
    amount: (amount || eventData.amount) ?? null,
    basePrice: pricePerTerm,
    bitwise: getSaveCardBitwise(state),
    creditCardType: getCCType(state),
    currency,
    discountCampaign: eventData.discountCampaign,
    duration: eventData.duration,
    errorMessage: eventData.errorMessage,
    from: eventData.from,
    likesNum: getLikesYouCount(state) || 0,
    pageType: eventData.pageType,
    pageVersion: eventData.pageVersion,
    paymentMethod: eventData.paymentMethod || getPaymentMethod(state),
    paywallVersion: eventData.paywallVersion,
    products: productsToObject(eventData.products),
    promoCode: eventData.promoCode,
    required3ds: eventData.required3ds,
    // revenueSessionId: eventData.revenueSessionId, TODO add in phase 2
    sku: (getProductSkuId(product, paymentType) || eventData.productId) ?? null,
    // sourceSessionId: eventData.sourceSessionId, TODO add in phase 2
    sourceSessionEvent: eventData.sourceSessionEvent || SOURCE_SESSION_EVENT_REVENUE,
    upsell: eventData.upsell,
    templateUuids: eventData.templateUuids,
  };

  if (eventData.type) {
    const isSubscription = PRODUCT_TYPES_ALL_SUBSCRIPTION.includes(eventData.type);
    return {
      ...result,
      amountUnit: isSubscription ? termsUnit : null,
      category: isSubscription ? PURCHASE_TYPE_SUBSCRIPTION : PURCHASE_TYPE_CONSUMABLE,
      subscription: isSubscription ? getSubscriptionValue(eventData.type) : 0,
      type: analyticsProductType[eventData.type],
    };
  }
  return result;
};

export const getRevenuePurchaseFlowGAData = (productType: string) => {
  const name = analyticsProductType[productType];
  const isSubscription = PRODUCT_TYPES_ALL_SUBSCRIPTION.includes(productType);
  const namePrefix = isSubscription ? I13N_CATEGORY_SUBSCRIPTION : I13N_CATEGORY_CONSUMABLES;
  return {
    category: isSubscription ? PURCHASE_TYPE_SUBSCRIPTION : PURCHASE_TYPE_CONSUMABLE,
    name: name && `${namePrefix}.${name}`,
  };
};

export function getCommonPromoData(state: GlobalStateType) {
  return {
    ...getCommonData(state),
    promoSource: getIncentivePromoSource(state),
    promoCode: getIncentivePromoCode(state),
    campaignName: getIncentiveCampaignName(state),
    campaignId: getIncentiveCampaignId(state),
  };
}

export function getCommonSendSwipeNoteData(state: GlobalStateType) {
  const currentRec = getCurrentRec(state);
  const swipedMediaIndex = getProfilesActivePhotoIndexById(state, currentRec?._id);
  const photos = currentRec?.photos || [];
  const swipedMediaId = photos?.[swipedMediaIndex]?.id;
  return {
    otherId: currentRec?._id,
    swipedMediaIndex,
    swipedMediaId,
    ...getCommonData(state),
  };
}

export function getCommonCheckoutPageData(state: GlobalStateType) {
  return {
    ...getCommonPromoData(state),
    isCCRequired: getIncentiveCCRequired(state),
    isIntroPricing:
      getIsIntroPriceIncentiveCampaign(state) || getHasIntroPriceIncentiveSubscription(state),
    isFreeTrial:
      getIsFreeTrialIncentiveCampaign(state) || getHasFreeTrialIncentiveSubscription(state),
  };
}

export function getRevenuePurchaseFlowCheckoutPageData(
  eventData: any = {},
  state: GlobalStateType
) {
  const type = eventData.type ?? eventData.product?.productType;
  const discountCampaign = eventData.product?.campaign;

  return {
    ...getCommonCheckoutPageData(state),
    action: eventData.action,
    discountCampaign,
    errorMessage: eventData.errorMessage,
    funnelName: eventData.funnelName,
    pageType: eventData.pageType,
    pageVersion: eventData.pageVersion || PAGE_VERSION_CHECKOUT,
    paymentMethod: eventData.paymentMethod || getPaymentMethod(state),
    sourceSessionEvent: eventData.sourceSessionEvent || SOURCE_SESSION_EVENT_REVENUE,
    type: type && analyticsProductType[type],
  };
}
